
import { Component, Vue } from 'vue-property-decorator'
import {
  createMcpKey,
  getMcpCalls,
  getMcpKeys,
  getMcpSettings,
  resetMcpKeysPii,
  revokeMcpKey,
  setMcpKeyPii,
  setMcpSettings,
  McpCall,
  McpCallTotals,
  McpKey,
  PiiMode
} from '@/api/mcp'

@Component({ name: 'Mcp' })
export default class extends Vue {
  private loading = false
  private callsLoading = false
  private settingsLoading = false
  private creating = false

  private keys: McpKey[] = []
  private calls: McpCall[] = []
  private totals: McpCallTotals | null = null

  private callFilter = { subject: '', toolName: '', onlyProblems: false }

  private toolNames = [
    'whoami', 'search_users', 'get_user_360', 'explain_purchase',
    'device_status', 'list_pending_grants', 'list_trainees'
  ]

  private piiEnabled = false
  private environment = 'test'
  private overrides: { forcedOn: number, forcedOff: number } | null = null

  private createVisible = false
  private keyVisible = false
  private issuedKey = ''

  private form = { email: '', label: '', expiresInDays: 365, piiMode: 'inherit' as PiiMode }

  private get endpoint() {
    // Taken from the panel's own API base so test and production each show
    // their own address. A command copied from the wrong environment fails in
    // a confusing way, and this is the cheapest place to prevent it.
    const base = String(process.env.VUE_APP_BASE_API || '').replace(/\/$/, '')
    return base ? base + '/mcp' : '/api/v2/mcp'
  }

  private get addCommand() {
    const name = this.environment === 'production' ? 'wewalk' : 'wewalk-test'
    return [
      'claude mcp add --transport http ' + name + ' \\',
      '  ' + this.endpoint + ' \\',
      '  --header "Authorization: Bearer ' + (this.issuedKey || '<anahtar>') + '"'
    ].join('\n')
  }

  private created() {
    this.fetch()
    this.fetchCalls()
    this.fetchSettings()
  }

  private async fetch() {
    this.loading = true
    try {
      const res: any = await getMcpKeys()
      this.keys = res.data.data.keys
      this.environment = res.data.data.environment
    } catch (err: any) {
      this.$message.error(this.msg(err))
    } finally {
      this.loading = false
    }
  }

  private async fetchCalls() {
    this.callsLoading = true
    try {
      const res: any = await getMcpCalls({
        limit: 100,
        subject: this.callFilter.subject || undefined,
        toolName: this.callFilter.toolName || undefined,
        onlyProblems: this.callFilter.onlyProblems || undefined
      })
      this.calls = res.data.data.calls
      this.totals = res.data.data.lastSevenDays
    } catch (err: any) {
      this.$message.error(this.msg(err))
    } finally {
      this.callsLoading = false
    }
  }

  private async fetchSettings() {
    this.settingsLoading = true
    try {
      const res: any = await getMcpSettings()
      this.piiEnabled = res.data.data.piiEnabled
      this.overrides = res.data.data.overrides || null
    } catch (err: any) {
      this.$message.error(this.msg(err))
    } finally {
      this.settingsLoading = false
    }
  }

  private confirmPii(value: boolean) {
    if (!value) {
      this.savePii(false)
      return
    }
    // Only the "on" direction asks. Turning personal data off is always safe;
    // turning it on is the decision worth a second of thought.
    this.$confirm(
      this.$t('mcp.piiConfirmBody') as string,
      this.$t('mcp.piiConfirmTitle') as string,
      {
        confirmButtonText: this.$t('mcp.continue') as string,
        cancelButtonText: this.$t('mcp.cancel') as string,
        type: 'warning'
      }
    )
      .then(() => this.savePii(true))
      .catch(() => { this.piiEnabled = false })
  }

  private async savePii(enabled: boolean) {
    try {
      await setMcpSettings(enabled)
      this.$message.success(this.$t('mcp.saved') as string)
    } catch (err: any) {
      this.$message.error(this.msg(err))
      this.fetchSettings()
    }
  }

  private openCreate() {
    this.form = { email: '', label: '', expiresInDays: 365, piiMode: 'inherit' }
    this.createVisible = true
  }

  private confirmKeyPii(row: McpKey, mode: PiiMode) {
    // Only turning personal data on for a key asks. Narrowing is always safe;
    // widening is the decision worth a beat of thought.
    const widening = mode === 'on'
    const run = async() => {
      try {
        await setMcpKeyPii(row.id, mode)
        this.$message.success(this.$t('mcp.saved') as string)
        this.fetch()
        this.fetchSettings()
      } catch (err: any) {
        this.$message.error(this.msg(err))
        this.fetch()
      }
    }

    if (!widening) {
      run()
      return
    }

    this.$confirm(
      this.$t('mcp.keyPiiConfirmBody', { label: row.label }) as string,
      this.$t('mcp.piiConfirmTitle') as string,
      {
        confirmButtonText: this.$t('mcp.continue') as string,
        cancelButtonText: this.$t('mcp.cancel') as string,
        type: 'warning'
      }
    )
      .then(run)
      .catch(() => this.fetch())
  }

  private confirmResetPii() {
    this.$confirm(
      this.$t('mcp.resetPiiConfirmBody') as string,
      this.$t('mcp.resetPii') as string,
      {
        confirmButtonText: this.$t('mcp.continue') as string,
        cancelButtonText: this.$t('mcp.cancel') as string,
        type: 'warning'
      }
    )
      .then(async() => {
        const res: any = await resetMcpKeysPii()
        this.$message.success(
          this.$t('mcp.resetPiiDone', { n: res.data.data.reset }) as string
        )
        this.fetch()
        this.fetchSettings()
      })
      .catch(() => undefined)
  }

  private async create() {
    if (!this.form.email || !this.form.label) {
      this.$message.warning(this.$t('mcp.fillAll') as string)
      return
    }
    this.creating = true
    try {
      const res: any = await createMcpKey(this.form)
      this.issuedKey = res.data.data.key
      this.createVisible = false
      this.keyVisible = true
      this.fetch()
    } catch (err: any) {
      this.$message.error(this.msg(err))
    } finally {
      this.creating = false
    }
  }

  private copyKey() {
    const el = document.createElement('textarea')
    el.value = this.issuedKey
    document.body.appendChild(el)
    el.select()
    document.execCommand('copy')
    document.body.removeChild(el)
    this.$message.success(this.$t('mcp.copied') as string)
  }

  private confirmRevoke(row: McpKey) {
    this.$confirm(
      this.$t('mcp.revokeConfirmBody', { label: row.label }) as string,
      this.$t('mcp.revokeConfirmTitle') as string,
      {
        confirmButtonText: this.$t('mcp.revoke') as string,
        cancelButtonText: this.$t('mcp.cancel') as string,
        type: 'warning'
      }
    )
      .then(async() => {
        await revokeMcpKey(row.id)
        this.$message.success(this.$t('mcp.revoked') as string)
        this.fetch()
      })
      .catch(() => undefined)
  }

  private shortDate(value: string) {
    if (!value) return '-'
    const d = new Date(value)
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    })
  }

  private fullArgs(args: any) {
    if (!args) return '-'
    return JSON.stringify(args)
  }

  private msg(err: any) {
    return err?.response?.data?.status?.message || err?.message || (this.$t('mcp.failed') as string)
  }
}
